<script setup lang="ts">

import {onMounted, onUnmounted, ref} from "vue";

const widgetToken = '92c722978e7e4c329d645b67ecd18fdc';
const scriptSrc = 'https://my.anyday.io/price-widget/anyday-price-widget.js'

const props = defineProps<{
	price: number | string
}>()

const widget = ref<HTMLElement | null>(null)
const activationSelectors = [
	'.anyday-clickable',
	'.anyday-widget-container',
	'button:not([disabled])',
	'a[href]',
	'[role="button"]',
	'[tabindex]:not([tabindex="-1"])'
]

function loadScript() {
	if (document.querySelector(`script[src="${scriptSrc}"]`)) return
	const script = document.createElement('script')
	script.src = scriptSrc
	script.type = 'module'
	script.async = true
	document.head.appendChild(script)
}

onMounted(() => {
	loadScript()
	document.addEventListener('keydown', handleDocumentKeydown)
})

onUnmounted(() => {
	document.removeEventListener('keydown', handleDocumentKeydown)
})

function getActionTarget(root: ParentNode | null) {
	if (!root) return null

	for (const selector of activationSelectors) {
		const target = root.querySelector(selector)
		if (target instanceof HTMLElement && target !== widget.value) {
			return target
		}
	}

	return null
}

function dispatchActivation(target: HTMLElement) {
	target.dispatchEvent(new MouseEvent('click', {
		bubbles: true,
		cancelable: true,
		composed: true
	}))

	if (typeof target.click === 'function') {
		target.click()
	}
}

function closeAnydayDialog() {
	const overlay = document.querySelector('.anyday-overlay-mask')
	if (!overlay) return

	const closeButton = overlay.querySelector('.anyday-dialog-close')
	if (closeButton instanceof HTMLElement) {
		dispatchActivation(closeButton)
	}
}

function handleDocumentKeydown(event: KeyboardEvent) {
	if (event.key !== 'Escape') return
	closeAnydayDialog()
}

function activateWidget() {
	const currentWidget = widget.value
	if (!currentWidget) return

	const shadowTarget = getActionTarget(currentWidget.shadowRoot)
	if (shadowTarget) {
		dispatchActivation(shadowTarget)
		return
	}

	const lightDomTarget = getActionTarget(currentWidget)
	if (lightDomTarget) {
		dispatchActivation(lightDomTarget)
		return
	}

	dispatchActivation(currentWidget)
}

function handleKeydown(event: KeyboardEvent) {
	if (event.key !== 'Enter' && event.key !== ' ') return

	event.preventDefault()
	activateWidget()
}

</script>

<template>
	<anyday-price-widget
		ref="widget"
		theme="light"
		currency="DKK"
		price-selector=".js-product-price"
		:price="price"
		price-format-locale="da-DK"
		locale="da-DK"
		:token="widgetToken"
		role="button"
		tabindex="0"
		@keydown="handleKeydown"
		fully-clickable="true"/>
</template>

